<script>
import axios from "axios";
import Swal from "sweetalert2";
import { getBase } from "@/services/http";
import EmployeeAPI from "@/services/employee/employeeAPI";

export default {
    props: {
        modalID: {
            type: String,
            default: null,
            required: true,
        },
        // Id de la empresa. Se pasa desde la vista de edicion para no depender de
        // que el store de Vuex este poblado (se pierde al refrescar / entrar por URL).
        companyId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            company: this.$store.getters["company/getCompany"].id ? this.$store.getters["company/getCompany"] : { id: null, uuid: null },
            loading: true,
            file: null,
            uploading: false,
            // Reporte de validacion devuelto por el backend (HTTP 422)
            errors: [],
            reportMessage: "",
            totalRows: 0,
            totalErrors: 0,
        }
    },
    computed: {
        // Prioriza la prop (id de la empresa de la vista) y cae al store si no viene.
        resolvedCompanyId() {
            if (this.companyId) return this.companyId;
            const stored = this.$store.getters["company/getCompany"];
            return stored && stored.id ? stored.id : null;
        },
    },
    methods: {
        handleFileChange(event) {
            // Validar si el archivo está siendo capturado
            this.file = event.target.files[0];
            // Al elegir un nuevo archivo se limpia el reporte anterior
            this.clearReport();
        },

        clearReport() {
            this.errors = [];
            this.reportMessage = "";
            this.totalRows = 0;
            this.totalErrors = 0;
        },

        // Descarga un archivo de ejemplo con 3 colaboradores validos, listo para
        // subir tal cual y probar la carga masiva sin diligenciar a mano.
        downloadExample() {
            const companyId = this.resolvedCompanyId;
            if (!companyId) return;
            window.location.href = EmployeeAPI.getTemplateUrl(companyId, 3);
        },

        async uploadFile() {
            try {
                if (!this.file) {
                    Swal.fire({
                        title: "Seleccione un archivo",
                        text: "Debe elegir el archivo de carga masiva (.xlsx) antes de continuar.",
                        icon: "info",
                        confirmButtonClass: "btn btn-confirm mt-2",
                    });
                    return;
                }

                const companyId = this.resolvedCompanyId;
                if (!companyId) {
                    Swal.fire({
                        title: "Empresa no identificada",
                        text: "No se pudo determinar la empresa. Recargue la página e intente de nuevo.",
                        icon: "warning",
                        confirmButtonClass: "btn btn-confirm mt-2",
                    });
                    return;
                }

                this.uploading = true; // Desactivar el botón mientras se sube el archivo
                this.clearReport();

                // Crear el FormData y adjuntar el archivo
                const formData = new FormData();
                formData.append('file', this.file);

                // Usa la misma base URL que el resto de la app (dev/testing/prod segun el entorno).
                const response = await axios.post(`${getBase()}upload-employee/${companyId}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.status === 200) {
                    Swal.fire({
                        title: "¡Carga masiva realizada con éxito!",
                        text: response.data && response.data.total
                            ? `Se registraron ${response.data.total} colaboradores.`
                            : "¡Carga masiva realizada con éxito!",
                        icon: "success",
                        confirmButtonClass: "btn btn-confirm mt-2",
                    });
                    window.location.reload();
                }

            } catch (error) {
                if (error.response) {
                    const data = error.response.data || {};
                    // 422 con reporte de validacion: mostramos el detalle fila/campo/motivo.
                    if (error.response.status === 422 && Array.isArray(data.errors) && data.errors.length) {
                        this.errors = data.errors;
                        this.reportMessage = data.message || "El archivo tiene errores. No se registró ningún colaborador.";
                        this.totalRows = data.total_filas || 0;
                        this.totalErrors = data.total_errores || data.errors.length;
                    } else {
                        // Otros errores del servidor (incluye 422 sin lista, 400, 500)
                        Swal.fire({
                            title: "¡Error en la carga masiva!",
                            text: data.message || "Error inesperado",
                            icon: "warning",
                            confirmButtonClass: "btn btn-confirm mt-2",
                        });
                    }
                } else if (error.request) {
                    // Si no se recibe ninguna respuesta (problema en la conexión o CORS)
                    Swal.fire({
                        title: "¡Error en la carga masiva!",
                        text: "No se recibió respuesta del servidor",
                        icon: "warning",
                        confirmButtonClass: "btn btn-confirm mt-2",
                    });
                } else {
                    // Otros errores
                    Swal.fire({
                        title: "¡Error en la carga masiva!",
                        text: error.message,
                        icon: "warning",
                        confirmButtonClass: "btn btn-confirm mt-2",
                    });
                }
            } finally {
                this.uploading = false; // Volver a habilitar el botón
            }
        }
    }
}
</script>

<template>
    <div>
        <button v-b-modal.cargaMasivaModal type="button" class="btn btn-primary m-l-5 ml-1">
            Carga Masiva Colaboradores
        </button>
        <b-modal id="cargaMasivaModal" title="Carga Masiva" title-class="font-18" dialog-class="modal-full-width"
            hide-footer>
            <div>
                <div class="row">
                    <div class="col-lg d-flex justify-content-center">
                        <div class="mr-3">
                            <input type="file" accept=".xlsx,.xls" @change="handleFileChange" />
                        </div>
                        <div class="">
                            <button class="btn btn-outline-primary" type="submit" :disabled="uploading" @click="uploadFile">
                                <span v-if="uploading">Procesando...</span>
                                <span v-else>Carga masiva</span>
                            </button>
                        </div>
                    </div>
                </div>
                <br />
                <div class="row">
                    <div class="col-lg text-center">
                        <button type="button" class="btn btn-link p-0" @click="downloadExample">
                            <i class="bx bx-download"></i> Descargar archivo de ejemplo (3 registros de prueba)
                        </button>
                    </div>
                </div>
                <div class="row">
                    <div class="col-lg d-flex justify-content-center">
                        <div class="mr-3 text-center">
                            <label for=""><strong>"Por favor, al completar la información en los campos 'DEPARTAMENTO' y 'MUNICIPIO', asegúrese de que el municipio ingresado corresponda realmente al departamento diligenciado."</strong></label>
                        </div>
                    </div>
                </div>

                <!-- Reporte de errores de validacion -->
                <div class="row mt-3" v-if="errors.length">
                    <div class="col-lg">
                        <div class="alert alert-danger" role="alert">
                            <strong>{{ reportMessage }}</strong>
                            <div class="mt-1">
                                Se revisaron {{ totalRows }} fila(s) y se encontraron {{ totalErrors }} error(es).
                                Corrija el archivo y vuelva a subirlo.
                            </div>
                        </div>
                        <div style="max-height: 45vh; overflow: auto;">
                            <table class="table table-sm table-bordered table-striped mb-0">
                                <thead class="thead-light">
                                    <tr>
                                        <th style="width: 70px;">Fila</th>
                                        <th>Columna</th>
                                        <th>Valor</th>
                                        <th>Motivo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(e, idx) in errors" :key="idx">
                                        <td class="text-center">{{ e.fila }}</td>
                                        <td>{{ e.campo }}</td>
                                        <td>{{ e.valor }}</td>
                                        <td>{{ e.motivo }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

        </b-modal>
    </div>
</template>
